import type { Period, QuarterAdvisorCheck, QuarterSummary, ReportableQuarter } from '../../api/types';
import { monthName, periodLabel } from '../../lib/format';

/** Quarters are identified by their first month, which is what the user picks. */
export const startKey = ({ year, month }: Period) => `${year}-${month}`;

const indexOf = ({ year, month }: Period) => year * 12 + month - 1;

export const quarterLabel = (q: { start: Period; end: Period }) =>
  q.start.year === q.end.year
    ? `${monthName(q.start.month)} – ${monthName(q.end.month)} ${q.end.year}`
    : `${periodLabel(q.start)} – ${periodLabel(q.end)}`;

export interface StartMonthGroup {
  year: number;
  quarters: ReportableQuarter[];
}

/** The quarter ending in `end` (how report batches are stored). */
export function quarterEndingAt(end: Period): { start: Period; end: Period } {
  const startIndex = indexOf(end) - 2;
  return { start: { year: Math.floor(startIndex / 12), month: (startIndex % 12) + 1 }, end };
}

export const samePeriod = (a: Period, b: Period) => indexOf(a) === indexOf(b);

/** Reportable quarters grouped by the year of their first month, newest first. */
export function groupByStartYear(quarters: ReportableQuarter[]): StartMonthGroup[] {
  const sorted = [...quarters].sort((a, b) => indexOf(b.start) - indexOf(a.start));
  const groups: StartMonthGroup[] = [];
  for (const quarter of sorted) {
    const last = groups.at(-1);
    if (last?.year === quarter.start.year) last.quarters.push(quarter);
    else groups.push({ year: quarter.start.year, quarters: [quarter] });
  }
  return groups;
}

/** The quarter starting at `key`, or the most recent one when there is no valid selection. */
export function selectQuarter(quarters: ReportableQuarter[], key: string | null): ReportableQuarter | null {
  const newest = groupByStartYear(quarters)[0]?.quarters[0] ?? null;
  return quarters.find((q) => startKey(q.start) === key) ?? newest;
}

// --- Month picker ------------------------------------------------------------

export interface MonthCell {
  month: number;
  /** The quarter that starts in this month, when it can be reported. */
  quarter: ReportableQuarter | null;
  /** First month of the selected quarter. */
  selected: boolean;
  /** Second or third month of the selected quarter. */
  inSelectedRange: boolean;
  /** Part of the quarter under the pointer (before clicking). */
  inHoverRange: boolean;
}

const covers = (q: { start: Period; end: Period } | null, index: number) =>
  q !== null && index >= indexOf(q.start) && index <= indexOf(q.end);

export function monthCells(
  year: number,
  quarters: ReportableQuarter[],
  selected: ReportableQuarter | null,
  hovered: ReportableQuarter | null,
): MonthCell[] {
  const byStart = new Map(quarters.map((q) => [startKey(q.start), q]));
  return Array.from({ length: 12 }, (_, i) => {
    const period = { year, month: i + 1 };
    const index = indexOf(period);
    const isStart = selected !== null && indexOf(selected.start) === index;
    return {
      month: period.month,
      quarter: byStart.get(startKey(period)) ?? null,
      selected: isStart,
      inSelectedRange: !isStart && covers(selected, index),
      inHoverRange: covers(hovered, index),
    };
  });
}

/** Years the picker can show, oldest first. */
export function pickerYears(quarters: ReportableQuarter[]): number[] {
  return [...new Set(quarters.map((q) => q.start.year))].sort((a, b) => a - b);
}

// --- Data review -----------------------------------------------------------------

export interface DataCheck {
  id: string;
  /** warning: changes a figure or a whole section of the PDF; info: cosmetic or expected. */
  tone: 'warning' | 'info';
  title: string;
  consequence: string;
  advisors: string[];
  /** Every advisor of the quarter is affected. */
  everyone: boolean;
  fix: { label: string; to: string };
}

const monthLink = ({ year, month }: Period) => `/datos?anio=${year}&mes=${month}`;
const hasPeriod = (list: Period[], period: Period) => list.some((p) => indexOf(p) === indexOf(period));

/**
 * Gaps in the quarter's data, described by their effect on the PDF. They are
 * informative only: the reports can always be generated.
 */
export function buildChecks(summary: QuarterSummary): DataCheck[] {
  const checks: DataCheck[] = [];
  const total = summary.advisors.length;
  const add = (
    id: string,
    tone: DataCheck['tone'],
    affected: QuarterAdvisorCheck[],
    title: string,
    consequence: string,
    fix: DataCheck['fix'],
  ) => {
    if (affected.length === 0) return;
    checks.push({
      id,
      tone,
      title,
      consequence,
      advisors: affected.map((a) => a.fullName),
      everyone: affected.length === total,
      fix,
    });
  };
  const correctIn = (period: Period) => ({ label: `Completar ${periodLabel(period)}`, to: monthLink(period) });

  for (const { period } of summary.months) {
    add(
      `minimum-${startKey(period)}`,
      'warning',
      summary.advisors.filter((a) => hasPeriod(a.missingMinimum, period)),
      `Sin mínimo esperable en ${monthName(period.month)}`,
      'El total del Art. 2.8 solo suma los meses que tienen mínimo, así que el mínimo trimestral saldrá más bajo.',
      correctIn(period),
    );
  }
  for (const { period } of summary.months.slice(1)) {
    add(
      `activity-${startKey(period)}`,
      'warning',
      summary.advisors.filter((a) => hasPeriod(a.withoutActivity, period)),
      `Sin actividad en ${monthName(period.month)}`,
      'La columna de KPIs de ese mes saldrá vacía en su informe.',
      correctIn(period),
    );
  }
  for (const { period } of summary.months) {
    add(
      `target-${startKey(period)}`,
      'info',
      summary.advisors.filter((a) => hasPeriod(a.missingTarget, period)),
      `Sin objetivo en ${monthName(period.month)}`,
      'Sus ventas de ese mes no pueden salir en verde: el verde exige superar el objetivo.',
      correctIn(period),
    );
  }

  const next = summary.next;
  const nextName = periodLabel(next);
  add(
    'next-none',
    'info',
    summary.advisors.filter((a) => a.nextMonthMinimum === null && a.nextMonthTarget === null),
    `Sin mínimo ni objetivo de ${nextName}`,
    'Su informe no llevará el banner morado del mes siguiente.',
    correctIn(next),
  );
  add(
    'next-target',
    'info',
    summary.advisors.filter((a) => a.nextMonthMinimum !== null && a.nextMonthTarget === null),
    `Sin objetivo de ${nextName}`,
    'El banner del mes siguiente mostrará solo el mínimo esperable.',
    correctIn(next),
  );
  add(
    'next-minimum',
    'info',
    summary.advisors.filter((a) => a.nextMonthMinimum === null && a.nextMonthTarget !== null),
    `Sin mínimo esperable de ${nextName}`,
    'El banner del mes siguiente mostrará solo el objetivo.',
    correctIn(next),
  );
  add(
    'team',
    'info',
    summary.advisors.filter((a) => a.teamCode === null),
    'Sin equipo asignado',
    'La cabecera de su informe no mostrará el código de equipo.',
    { label: 'Asignar en Asesores', to: '/asesores' },
  );

  return checks.sort((a, b) => (a.tone === b.tone ? 0 : a.tone === 'warning' ? -1 : 1));
}
